import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import {
  fetchLearningPath,
  fetchVideoProgress,
  fetchQuizProgress,
} from "./contentApi";

const initialState = {
  status: "idle",
  initialContentLoadComplete: false,
  initialProgressLoadComplete: false,
  learningPath: [],
};

export const fetchLearningPathAsync = createAsyncThunk(
  "content/fetchLearningPathAsync",
  async ({ db }) => {
    const activeCampaign = await fetchLearningPath({ db });
    return activeCampaign;
  }
);

export const fetchVideoProgressAsync = createAsyncThunk(
  "content/fetchVideoProgressAsync",
  async ({ db, user }) => {
    const videoProgress = await fetchVideoProgress({ db, user });
    return videoProgress;
  }
);

export const fetchQuizProgressAsync = createAsyncThunk(
  "content/fetchQuizProgressAsync",
  async ({ db, user }) => {
    const quizProgress = await fetchQuizProgress({ db, user });
    return quizProgress;
  }
);

export const setLastWatchedVideoAsync = createAsyncThunk(
  "content/setLastWatchedVideoAsync",
  async ({ video }) => {
    return { video };
  }
);

export const setNextVideoInSequenceAsync = createAsyncThunk(
  "content/setNextVideoInSequenceAsync",
  async () => {
    return true;
  }
);

export const setPrevVideoInSequenceAsync = createAsyncThunk(
  "content/setPrevVideoInSequenceAsync",
  async () => {
    return true;
  }
);

export const clearContentAsync = createAsyncThunk(
  "content/clearContentAsync",
  async () => {
    return true;
  }
);

export const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLearningPathAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(fetchLearningPathAsync.fulfilled, (state, action) => {
        state.learningPath = action.payload;
        state.initialContentLoadComplete = true;
        state.status = `idle`;
      })
      .addCase(fetchVideoProgressAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(fetchVideoProgressAsync.fulfilled, (state, action) => {
        let payload = action.payload;
        let learningPathState = [];

        /**
         * Loop through all stored learning path values in the application state. Store
         * each item in the state.learningPath array as a new object.
         */
        state.learningPath.forEach((element) => {
          let learningPath = { ...element };
          let learningPathProgress = undefined;

          /**
           * Filter learning path progress data coming back from fetchVideoProgress API call
           * and find all documents that match this doc by ID. Returns an array of all progress
           * docs that are relevant to the current learning path doc.
           */

          if (payload) {
            learningPathProgress = payload.filter(
              (progressDoc) => learningPath.id === progressDoc.learningPathId
            );
          }

          /**
           * If this learning path has a videos key, loop through each video. Filter relevant progress
           * docs to find entries that have the same id as this video. Append a progress key to the
           * video object.
           */
          if (learningPath.videos) {
            learningPath.videos.forEach((video) => {
              let videoProgress = undefined;
              let quizProgress = undefined;

              let videoComplete = false;
              let quizComplete = false;

              if (learningPathProgress) {
                videoProgress = learningPathProgress.filter(
                  (progress) =>
                    progress.type === "video" && progress.id === video.id
                );

                if (video.quiz) {
                  quizProgress = learningPathProgress.filter(
                    (progress) =>
                      progress.type === "quiz" && progress.videoId === video.id
                  );
                }
              }

              if (video.quiz && quizProgress.length === video.quiz.length) {
                video.quizComplete = true;
                quizComplete = true;
              }     
              
              if (video.quiz && quizProgress.length) {
                video.quizProgress = [...quizProgress]

                if (quizProgress.length === video.quiz.length) {
                  video.quizComplete = true;
                  quizComplete = true;
                }
              }              

              if (
                videoProgress &&
                videoProgress[0] &&
                videoProgress[0].id === video.id
              ) {
                video.progress = {
                  percent: videoProgress[0].percent,
                  seconds: videoProgress[0].seconds,
                  timestamp: videoProgress[0].timestamp,
                };                
                
                if (videoProgress[0].percent === 1) {
                  videoComplete = true;                  
                }
              }              

              if (videoComplete && !video.quiz) {
                video.uiState = "complete";                
              } else if (videoComplete && video.quiz) {
                if (quizComplete) {
                  video.uiState = "complete";
                }
              }

              if (video.uiState !== "complete") {
                if (video.prerequisite) {
                  let learningPath = state.learningPath.filter(
                    (learningPath) =>
                      learningPath.id === video.prerequisite.learningPathId
                  );

                  if (learningPath && learningPath[0]) {
                    learningPath = learningPath[0];

                    if (learningPath.videos) {
                      let prerequisiteVideo = learningPath.videos.filter(
                        (prerequisite) =>
                          prerequisite.id === video.prerequisite.id
                      );

                      if (prerequisiteVideo && prerequisiteVideo[0]) {
                        const prerequisite = prerequisiteVideo[0];                                                                        
                        const videoProgress = prerequisite.progress;                        

                        if (videoProgress && videoProgress.percent === 1) {
                          if (prerequisite.quiz && prerequisite.quizComplete) {
                            state.nextVideo = video;
                            video.uiState = "open";
                          } else if ( prerequisite.quiz && !prerequisite.quizComplete ) {
                            video.uiState = "locked";
                          } else {
                            state.nextVideo = video;
                            video.uiState = "open";
                          }                          
                        } else {
                          video.uiState = "locked";
                        }                                                
                      }
                    }
                  }
                } else {
                  state.nextVideo = video;
                  video.uiState = "open";
                }
              } else if (learningPath._type === "finish") {
                if (learningPath.videos && learningPath.videos[0]) {
                  state.nextVideo = learningPath.videos[0];
                }
              }
            });
          }

          /**
           * Regardless of appended progress, add this document to the learningPathState array.
           */
          learningPathState.push(learningPath);
        });

        /**
         * Replace existing state with the learningPathState array, a copy of the original state
         * with video progress merged into each individual video entry.
         */
        state.learningPath = learningPathState;

        state.initialProgressLoadComplete = true;
        state.status = `idle`;
      })
      .addCase(fetchQuizProgressAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(fetchQuizProgressAsync.fulfilled, (state, { payload }) => {
        if (payload && payload.length) {
          let learningPathState = [];

          /**
           * Loop through all stored learning path values in the application state. Store
           * each item in the state.learningPath array as a new object.
           */
          state.learningPath.forEach((element) => {
            let learningPath = { ...element };

            /**
             * Filter learning path progress data coming back from fetchVideoProgress API call
             * and find all documents that match this doc by ID. Returns an array of all progress
             * docs that are relevant to the current learning path doc.
             */
            const learningPathProgress = payload.filter(
              (progressDoc) => learningPath.id === progressDoc.learningPathId
            );

            /**
             * If this learning path has a videos key, loop through each video. Filter relevant progress
             * docs to find entries that have the same id as this video. Append a progress key to the
             * video object.
             */
            if (learningPath.videos && learningPathProgress) {
              learningPath.videos.forEach((video) => {
                const quizProgress = learningPathProgress.filter(
                  (progress) => progress.id === video.id
                );

                if (video.quiz) {
                  quizProgress.forEach((progress) => {
                    const answer =
                      video.quiz[progress.questionIndex].answers[
                        progress.answerIndex
                      ];
                    if (answer) {
                      answer.isSelectedAnswer = true;
                      answer.selectedAt = progress.timestamp;
                    }
                  });
                }
              });
            }

            /**
             * Regardless of appended progress, add this document to the learningPathState array.
             */
            learningPathState.push(learningPath);
          });

          /**
           * Replace existing state with the learningPathState array, a copy of the original state
           * with video progress merged into each individual video entry.
           */
          state.learningPath = learningPathState;
        }

        state.status = `idle`;
      })
      .addCase(setLastWatchedVideoAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(setLastWatchedVideoAsync.fulfilled, (state, { payload }) => {
        state.lastWatchedVideo = payload.video;
        state.status = `idle`;
      })
      .addCase(setNextVideoInSequenceAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(setNextVideoInSequenceAsync.fulfilled, (state) => {
        const lastWatchedVideo = current(state.lastWatchedVideo);
        const currentState = current(state);

        if (lastWatchedVideo && lastWatchedVideo.learningPathId) {
          const learningPathId = lastWatchedVideo.learningPathId;
          const learningPath = currentState.learningPath.filter(
            (learningPath) => learningPath.id === learningPathId
          );

          if (learningPath && learningPath[0]) {
            const videos = learningPath[0].videos;

            /**
             * Course content: If the learning path of the last watched video includes more that one video we're
             * working with the course content. Use the prerequisite video to find the next video
             * in the sequence.
             */
            if (videos && videos.length > 1) {
              const currentVideoIndex = videos.findIndex(
                (video) => video.id === lastWatchedVideo.id
              );

              if (currentVideoIndex + 1 !== videos.length) {
                const video = videos[currentVideoIndex + 1];
                state.nextVideoInSequence = video;
              } else {
                let nextVideoInSequence = undefined;

                currentState.learningPath.map((nextLearningPath) => {
                  if (nextLearningPath.videos) {
                    nextLearningPath.videos.map((nextVideo) => {
                      if (nextVideo.prerequisite) {
                        if (nextVideoInSequence) return;

                        const prerequisiteid = nextVideo.prerequisite.id;

                        if (prerequisiteid === lastWatchedVideo.id) {
                          nextVideoInSequence = nextVideo;
                        }
                      }
                    });
                  }
                });

                if (nextVideoInSequence) {
                  state.nextVideoInSequence = nextVideoInSequence;
                }
              }

              /**
               * Finish: If the current state shows that the next video learning page matches the last learning path,
               * set the next link to the last video in the learning path with the type of "finish".
               */
            } else if (
              learningPath[0]._type === "finish" &&
              currentState.nextVideo.learningPathId === learningPath[0].id
            ) {
              if (learningPath[0].videos && learningPath[0].videos[0]) {
                state.nextVideoInSequence = learningPath[0].videos[0];
              }

              /**
               * Start / course content with only one video: If the learning path includes only one video, but the
               * learning path isn't the finish, find the next video by matching the prerequisites
               */
            } else {
              let nextVideoInSequence = undefined;

              currentState.learningPath.map((nextLearningPath) => {
                if (nextLearningPath.videos) {
                  nextLearningPath.videos.map((nextVideo) => {
                    if (nextVideo.prerequisite) {
                      if (nextVideoInSequence) return;

                      const prerequisiteid = nextVideo.prerequisite.id;
                      if (prerequisiteid === lastWatchedVideo.id) {
                        nextVideoInSequence = nextVideo;
                      }
                    }
                  });
                }
              });

              if (nextVideoInSequence) {
                state.nextVideoInSequence = nextVideoInSequence;
              }
            }
          }
        }

        state.status = `idle`;
      })
      .addCase(setPrevVideoInSequenceAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(setPrevVideoInSequenceAsync.fulfilled, (state) => {
        const lastWatchedVideo = current(state.lastWatchedVideo);
        const currentState = current(state);

        if (lastWatchedVideo && lastWatchedVideo.learningPathId) {
          const learningPathId = lastWatchedVideo.learningPathId;
          const learningPath = currentState.learningPath.filter(
            (learningPath) => learningPath.id === learningPathId
          );

          if (learningPath && learningPath[0]) {
            const videos = learningPath[0].videos;

            if (videos) {
              const currentVideoIndex = videos.findIndex(
                (video) => video.id === lastWatchedVideo.id
              );

              if (currentVideoIndex > 0) {
                const video = videos[currentVideoIndex - 1];
                state.prevVideoInSequence = video;
              } else if (lastWatchedVideo.prerequisite) {
                let prevVideoInSequence = undefined;

                currentState.learningPath.map((prevLearningPath) => {
                  if (prevLearningPath.videos) {
                    prevLearningPath.videos.map((prevVideo) => {
                      const currentVideoPrerequId =
                        lastWatchedVideo.prerequisite.id;

                      if (prevVideo.id === currentVideoPrerequId) {
                        prevVideoInSequence = prevVideo;
                      }
                    });
                  }
                });

                if (prevVideoInSequence) {
                  state.prevVideoInSequence = prevVideoInSequence;
                }
              } else {
                state.prevVideoInSequence = null;
              }
            }
          }
        }

        state.status = `idle`;
      })
      .addCase(clearContentAsync.pending, (state) => {
        state.status = `loading`;
      })
      .addCase(clearContentAsync.fulfilled, (state) => {
        state.initialContentLoadComplete = false;
        state.initialProgressLoadComplete = false;
        state.learningPath = [];
        state.nextVideo = undefined;
        state.lastWatchedVideo = undefined;
        state.nextVideoInSequence = undefined;
        state.prevVideoInSequence = undefined;
        state.status = "idel";
      });
  },
});

export default contentSlice.reducer;
